import { Select as ChakraSelect } from "@chakra-ui/react";
import { useState } from "react";
import styled from "styled-components";
import { Modal } from "@permisso/permisso-components";
import { colors, Icon as PCIcon } from "@permisso/permisso-components";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { T } from "src/apps/components/i18n/Translation";
import { ActorAccess } from "src/contexts/documentationArea/domain/ActorAccess";
import { useActorUserRevokeAccess } from "src/apps/hooks/useActorUserRevokeAccess";

const TITLE = "documentation_area.access_actions.revoke_access.modal.title";
const DESCRIPTION = "documentation_area.access_actions.revoke_access.modal.description";
const PLACEHOLDER = "documentation_area.access_actions.revoke_access.modal.placeholder";

type ActorUserRevokeAccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  accesses: ActorAccess[];
};

export const ActorUserRevokeAccessModal = ({
  isOpen,
  onClose,
  accesses,
}: ActorUserRevokeAccessModalProps) => {
  const { t } = useTranslations();
  const { isMobile } = useIsMedia();
  const [isAccessListOpen, setIsAccessListOpen] = useState(false);
  const closeModal = () => {
    setIsAccessListOpen(false);
    onClose();
  };
  const { userLinkId, setUserLinkId, selectedAccess, isPending, resetAndClose, revokeAccess } =
    useActorUserRevokeAccess(accesses, closeModal);

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={<T k={TITLE} />}
      description={<T k={DESCRIPTION} />}
      variant="warning"
      hasIcon={false}
      breakpoint={isMobile ? "mobile" : "desktop"}
      placement={isMobile ? "bottom" : "floating"}
      primaryActionLabel={<T k="common.revoke" />}
      onPrimaryAction={revokeAccess}
      isPrimaryActionDisabled={!selectedAccess}
      isPrimaryActionLoading={isPending}
      secondaryActionLabel={<T k="common.cancel" />}
      onSecondaryAction={resetAndClose}
    >
      {isMobile ? (
        <MobileAccessSelector>
          <MobileAccessTrigger
            type="button"
            aria-label={t(DESCRIPTION)}
            aria-expanded={isAccessListOpen}
            aria-controls="revoke-access-options"
            onClick={() => setIsAccessListOpen((isOpen) => !isOpen)}
          >
            <span>{selectedAccess?.email ?? t(PLACEHOLDER)}</span>
            <PCIcon name="chevron-down" size="s16" color="grey700" />
          </MobileAccessTrigger>
          {isAccessListOpen ? (
            <MobileAccessOptions
              id="revoke-access-options"
              role="group"
              aria-label={t(DESCRIPTION)}
            >
              {accesses.map((access) => {
                const isSelected = access.userLinkId === userLinkId;
                return (
                  <MobileAccessOption
                    key={access.userLinkId}
                    type="button"
                    aria-pressed={isSelected}
                    $isSelected={isSelected}
                    onClick={() => {
                      setUserLinkId(access.userLinkId);
                      setIsAccessListOpen(false);
                    }}
                  >
                    {access.email}
                  </MobileAccessOption>
                );
              })}
            </MobileAccessOptions>
          ) : null}
        </MobileAccessSelector>
      ) : (
        <ChakraSelect
          value={userLinkId}
          placeholder={t(PLACEHOLDER)}
          aria-label={t(DESCRIPTION)}
          focusBorderColor={colors.grey500}
          _focus={{ borderColor: colors.grey500, boxShadow: "none" }}
          _focusVisible={{ borderColor: colors.grey500, boxShadow: "none" }}
          onChange={(event) => setUserLinkId(event.target.value)}
        >
          {accesses.map((access) => (
            <option key={access.userLinkId} value={access.userLinkId}>
              {access.email}
            </option>
          ))}
        </ChakraSelect>
      )}
    </Modal>
  );
};

const MobileAccessSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MobileAccessTrigger = styled.button`
  display: flex;
  width: 100%;
  min-height: 2.5rem;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid ${colors.grey500};
  border-radius: 0.5rem;
  background: ${colors.grey0};
  color: ${colors.grey700};
  font-size: 0.875rem;
  text-align: left;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MobileAccessOptions = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 12rem;
  overflow-y: auto;
  border: 1px solid ${colors.grey400};
  border-radius: 0.5rem;
  background: ${colors.grey0};
`;

const MobileAccessOption = styled.button<{ $isSelected: boolean }>`
  display: flex;
  width: 100%;
  min-height: 2.75rem;
  align-items: center;
  padding: 0.75rem 1rem;
  border: 0;
  background-color: ${({ $isSelected }) => ($isSelected ? colors.grey300 : "transparent")};
  color: ${colors.grey800};
  font-size: 0.875rem;
  font-weight: ${({ $isSelected }) => ($isSelected ? 700 : 400)};
  line-height: 1.5;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${colors.grey300};
  }

  &:focus-visible {
    outline: 2px solid ${colors.grey700};
    outline-offset: -2px;
  }
`;
